import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { OPEN_RESOURCE_DETAIL_PANEL, CLOSE_RESOURCE_DETAIL_PANEL } from 'js/constants/actionTypes';
import {
  RESOURCE_TYPE_ATTR_TRANSFORM,
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_CREDENTIAL_GROUP,
  RESOURCE_TYPE_DATA_MAP,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
} from 'js/constants/connectionCategories';
import flowTypes from 'new/components/flows/types/flowTypes';
import { DataSourceDetailHeader } from 'js/components/data_sources/cards/DataSourceDetailHeader';
import { DataSinkDetailHeader } from 'js/components/data_sinks/cards/DataSinkDetailHeader';
import { DataSetDetailHeader } from 'js/components/data_sets/cards/DataSetDetailHeader';
import { AvailableDataSetDetailHeader } from 'js/components/data_sets_available/cards/AvailableDataSetDetailHeader';
import useEntities from 'new/state/entities/repository/useEntities';
import usePaging from 'new/state/paging/repository/usePaging';
import CardDetailView from 'js/components/elements/cards/CardDetailView';
import Portal from 'new/elements/Portal';
import ProjectDetailViewContainer from 'js/components/projects/cards/ProjectDetailViewContainer';
import { CredentialsDetailHeader } from 'js/components/credentials/cards/CredentialsDetailHeader';
import CredentialsGroupDetailHeader from 'js/components/credentials/cards/CredentialsGroupDetailHeader';
import { ReusableTransformDetailHeader } from 'js/components/reusableTransforms/cards/ReusableTransformDetailHeader';
import useResourceEntity from 'new/hooks/useResourceEntity';
import { useResourceHeaderTabs } from 'new/hooks/useResourceHeaderTabs';
import { ResourceDetailContext } from 'new/components/ResourceDetailHandler/context';
import { DataMapDetailHeader } from 'js/components/data_maps/cards/DataMapDetailHeader';
import StyledFixedPanel from 'new/components/StyledFixedPanel';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { PROJECT_DETAIL_VIEW } from 'js/constants/projects';

const detailContentHeader = {
  SOURCE: {
    v2: DataSourceDetailHeader,
  },
  DATA_SINK: {
    v2: DataSinkDetailHeader,
  },
  DATA_SET: {
    v2: DataSetDetailHeader,
    shared_v2: AvailableDataSetDetailHeader,
  },
  [RESOURCE_TYPE_SOURCE]: {
    v2: DataSourceDetailHeader,
  },
  [RESOURCE_TYPE_SINK]: {
    v2: DataSinkDetailHeader,
  },
  [RESOURCE_TYPE_DATA_SET]: {
    v2: DataSetDetailHeader,
    shared_v2: AvailableDataSetDetailHeader,
  },
  [RESOURCE_TYPE_CREDENTIALS]: {
    v2: CredentialsDetailHeader,
  },
  [RESOURCE_TYPE_CREDENTIAL_GROUP]: {
    v2: CredentialsGroupDetailHeader,
  },
  [RESOURCE_TYPE_ATTR_TRANSFORM]: {
    v2: ReusableTransformDetailHeader,
  },
  [RESOURCE_TYPE_DATA_MAP]: {
    v2: DataMapDetailHeader,
  },
};

export const ResourceDetailProvider = (props: PropsWithChildren<any>) => {
  const { children, resourceType, hideActions } = props;
  const dispatch = useAppDispatch();
  const [projectDetail, setProjectDetail] = useState(null);
  const [selectedCardData, setSelected] = useState(null);
  const [selectedCardMeta, setSelectedMeta] = useState(null);
  const [openInPortal, setOpenInPortal] = useState(false);

  const { data: entities } = useEntities();
  const { aclQueryMode } = useSelector((state: any) => state.app.listing);
  const { getMetaInfoById } = usePaging();
  const { sources, flowNodes } = entities;

  const entity = useResourceEntity(selectedCardData, resourceType);
  const selectedCardEntity =
    selectedCardData && entity ? { ...selectedCardData, ...entity } : undefined;

  const flow = flowNodes[selectedCardEntity?.origin_node_id];
  const tabs = useResourceHeaderTabs(selectedCardEntity, {
    flow,
    resourceType,
  });

  const dataSource = sources[flow?.data_source_id];

  const onOpenDetailClicked = useCallback((selectedCardData: any, selectedCardMeta: any) => {
    setSelectedMeta(selectedCardMeta);
    setSelected(selectedCardData);
    dispatch({ type: OPEN_RESOURCE_DETAIL_PANEL });
  }, []);

  const openDetailDrawerInPortal = useCallback((selectedCardData: any, selectedCardMeta: any) => {
    setOpenInPortal(true);
    setSelectedMeta(selectedCardMeta);
    setSelected(selectedCardData);
    dispatch({ type: OPEN_RESOURCE_DETAIL_PANEL });
  }, []);

  const onOpenProjectDetail = useCallback((project) => {
    setProjectDetail(project);
  }, []);

  function _getSelectedCardHeader() {
    const flowType = dataSource?.flow_type || flow?.flow_type;

    const isShared = selectedCardData?.dataSetType === 'shared';

    let key = 'v2';
    let showTransform = true;
    if (selectedCardData.type === 'DATA_SET') {
      if (isShared) {
        key = 'shared_v2';
      }
      if (
        flowType === flowTypes.replication &&
        [CONNECTION_CATEGORY_FILE, CONNECTION_CATEGORY_DATABASE].includes(
          getMetaInfoById(dataSource.source_type)?.connectionCategory,
        ) &&
        !isEmpty(selectedCardData.children)
      ) {
        showTransform = false;
      }
    }

    const HeaderContent =
      detailContentHeader[resourceType || selectedCardData.type || selectedCardData.resourceType][
        key
      ];

    // Get connectionOrVendorType for source headers to fix duplicate button logic
    let connectionOrVendorType = null;
    if (HeaderContent === DataSourceDetailHeader && dataSource) {
      connectionOrVendorType = getConnectionOrVendorType(
        dataSource,
        entities.vendors,
        'source_type',
        'vendor_id',
      );
    }

    return (
      <HeaderContent
        onCloseDrawer={_handleCloseDetailClicked}
        showPipelineButton={false}
        showTransform={showTransform}
        showSend={!isShared && showTransform}
        showMenu={!isShared}
        connectionOrVendorType={connectionOrVendorType}
      />
    );
  }

  function _getSelectedCardMeta() {
    return selectedCardMeta;
  }

  function _handleCloseDetailClicked() {
    setSelectedMeta(null);
    setSelected(null);
    setOpenInPortal(false);
    dispatch({ type: CLOSE_RESOURCE_DETAIL_PANEL });
  }

  const onCloseProject = () => {
    setProjectDetail(null);
  };

  const memoValue = useMemo(
    () => ({
      onOpenDetailClicked,
      openDetailDrawerInPortal,
      onOpenProjectDetail,
    }),
    [onOpenDetailClicked, onOpenProjectDetail, openDetailDrawerInPortal],
  );

  const content = selectedCardEntity ? (
    <div aria-label="card-detail-view-wrapper">
      <div style={{ zIndex: 1000 }} className="modal-fader" onClick={_handleCloseDetailClicked} />
      <StyledFixedPanel>
        <CardDetailView
          hideActions={hideActions}
          noHeaderPadding
          data={selectedCardEntity}
          tabs={tabs}
          detailHeaderContent={_getSelectedCardHeader()}
          getSourceMeta={_getSelectedCardMeta}
          aclQueryMode={aclQueryMode}
          onCloseDrawer={_handleCloseDetailClicked}
        />
      </StyledFixedPanel>
    </div>
  ) : null;

  return (
    <ResourceDetailContext.Provider value={memoValue}>
      {children}
      {projectDetail && (
        <ProjectDetailViewContainer
          id={projectDetail.id}
          type={projectDetail.type}
          onClose={onCloseProject}
        />
      )}

      {openInPortal ? <Portal>{content}</Portal> : content}
    </ResourceDetailContext.Provider>
  );
};
